/**
 * Central registry of imagery used across the public marketing site.
 *
 * All URLs point at Unsplash (configured in next.config.ts). Swapping these
 * for a real CDN later only requires editing this file — components reference
 * these constants, never raw URLs.
 *
 * Query params:
 *   q=80         -> quality
 *   auto=format  -> let Unsplash serve webp/avif
 *   fit=crop     -> crop to the requested box
 *   w=...        -> intrinsic width hint
 */

const U = (id: string, w = 1200) =>
  `https://images.unsplash.com/${id}?q=80&auto=format&fit=crop&w=${w}`;

export const siteImages = {
  hero: {
    main: U('photo-1522071820081-009f0129c71c', 1400), // team collaborating
    secondary: U('photo-1551434678-e076c223a692', 1000), // developers at work
  },

  about: {
    office: U('photo-1497366216548-37526070297c', 1400), // modern office
    team: U('photo-1521737604893-d14cc237f11d', 1200), // team meeting
    culture: U('photo-1522202176988-66273c2fd55f', 1200), // collaboration
  },

  // Keyed by service slug (matches Services.tsx urls)
  services: {
    'mobile-app-development': U('photo-1512941937669-90a1b58e7e9c', 900),
    'web-development': U('photo-1547658719-da2b51169166', 900),
    'ecommerce-development': U('photo-1556742049-0cfed4f6a45d', 900),
    'blockchain-development': U('photo-1639762681485-074b7f938ba0', 900),
    'ai-ml-services': U('photo-1620712943543-bcc4688e7485', 900),
    'devops-cloud': U('photo-1667372393119-3d4c48d07fc9', 900),
    'iot-embedded': U('photo-1518770660439-4636190af475', 900),
    cybersecurity: U('photo-1550751827-4bd374c3f58b', 900),
  } as Record<string, string>,

  // Industries (keyed by title lowercased + hyphenated handled in component)
  industries: {
    healthcare: U('photo-1576091160550-2173dba999ef', 800),
    finance: U('photo-1611974789855-9c2a0a7236a3', 800),
    retail: U('photo-1556742049-0cfed4f6a45d', 800),
    education: U('photo-1523050854058-8df90110c9f1', 800),
    manufacturing: U('photo-1581091226825-a6a2a5aee158', 800),
    logistics: U('photo-1566576912321-d58ddd7a6088', 800),
    realestate: U('photo-1560518883-ce09059eeffa', 800),
    entertainment: U('photo-1522869635100-9f4c5e86aa37', 800),
  } as Record<string, string>,

  // Solutions (keyed by route folder name)
  solutions: {
    'hospital-management': U('photo-1576091160399-112ba8d25d1f', 1100),
    'school-management': U('photo-1523050854058-8df90110c9f1', 1100),
    'college-management': U('photo-1562774053-701939374585', 1100),
    'gym-management': U('photo-1534438327276-14e5300c3a48', 1100),
    'marriage-bureau': U('photo-1511285560929-80b456fea0bc', 1100),
    lms: U('photo-1501504905252-473c47e087f8', 1100),
    'ecommerce-platform': U('photo-1556742502-ec7c0e9f34b1', 1100),
    'cab-service': U('photo-1549924231-f129b911e442', 1100),
    'on-demand-services': U('photo-1556740738-b6a63e27c4df', 1100),
    'real-estate-crm': U('photo-1560518883-ce09059eeffa', 1100),
    'crm-system': U('photo-1460925895917-afdab827c52f', 1100),
    'office-crm': U('photo-1497366811353-6870744d04b2', 1100),
  } as Record<string, string>,

  // Portfolio / case studies
  portfolio: [
    U('photo-1460925895917-afdab827c52f', 900), // analytics dashboard
    U('photo-1551288049-bebda4e38f71', 900), // charts
    U('photo-1498050108023-c5249f4df085', 900), // code
    U('photo-1517245386807-bb43f82c33c4', 900), // laptop
    U('photo-1555774698-0b77e0d5fac6', 900), // mobile app
    U('photo-1526628953301-3e589a6a8b74', 900), // devops
  ],

  // Blog fallback cover + author avatars
  blog: {
    fallbackCover: U('photo-1499750310107-5fef28a66643', 1200),
  },

  avatars: {
    a1: 'https://i.pravatar.cc/120?img=12',
    a2: 'https://i.pravatar.cc/120?img=32',
    a3: 'https://i.pravatar.cc/120?img=45',
    a4: 'https://i.pravatar.cc/120?img=5',
    a5: 'https://i.pravatar.cc/120?img=68',
  },
};

/**
 * Live demo URLs for solutions that have a hosted product.
 * Keyed by the solution route folder name. Add more as they go live.
 */
export const solutionDemoLinks: Record<string, string> = {
  'hospital-management': 'https://hospital.yoranova.com',
  'school-management': 'https://school.yoranova.com',
};

/** A tiny shimmer placeholder (base64) for next/image blur-up. */
export const shimmerDataURL = (w = 700, h = 475) => {
  const shimmer = `
<svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <linearGradient id="g">
      <stop stop-color="#e2e8f0" offset="20%" />
      <stop stop-color="#f1f5f9" offset="50%" />
      <stop stop-color="#e2e8f0" offset="70%" />
    </linearGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="#e2e8f0" />
  <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
  <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1.4s" repeatCount="indefinite" />
</svg>`;
  const toBase64 = (str: string) =>
    typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);
  return `data:image/svg+xml;base64,${toBase64(shimmer)}`;
};
